import { sealedIndexedDbCache } from '../vault/cache';
import { CloudVault } from '../vault/cloudVault';
import { writeFiles } from '../vault/sparseWriter';
import { queueSnapshot, setFlusher } from '../vault/writeQueue';
import { getSession, subscribe as subscribeSession } from './vaultSession';
import { announceCutOff, servePeers, takeWarmManifestVersion } from './tabHandshake';
import { freshnessSnapshot, markConflicts, markCurrent, markCutOff, watchFreshness } from './freshness';
import type { VaultSession } from './vaultSession';
import { CallableError } from '../backend/callables';
import { describeFailure } from './vaultFailure';
import { backendFor } from './vaultBackend';
import { getAuth } from './authSession';

/**
 * ONE vault for the tab, opened once per session and shared by every surface.
 *
 * It was a hook before, which meant every screen that called it built its own
 * CloudVault and ran its own sync against the same IndexedDB cache. Three
 * things came out of that, none of them obvious from a call site:
 *
 *  - Surfaces disagreed. Two instances sync at different moments, so the board
 *    and the command palette legitimately held different snapshots and
 *    reported different arc counts, which reads as broken sync.
 *  - N syncs where one would do, contending over a cache whose own comments
 *    describe what concurrent connections cost it.
 *  - The write flusher was set by whichever components happened to be mounted,
 *    and cleared on ANY of their unmounts — so navigating away from one screen
 *    could leave the queue unable to push for the others.
 *
 * A module store rather than a context because the session is already one, and
 * because the write queue needs the vault from outside React, which a context
 * cannot give it.
 */
export type VaultState =
  | { status: 'disconnected' }
  | { status: 'loading' }
  /** [stale] names why the last refresh failed, on a vault that is still
   *  usable from cache. Ready rather than error because the page works — but
   *  never silent: swallowing this is what let a browser whose credentials had
   *  died render a three-day-old board under a "synced" dot. */
  | { status: 'ready'; vault: CloudVault; stale: string | null }
  /** [detail] is the underlying failure, shown small and verbatim. A reader
   *  cannot act on it, but it is the difference between reporting "it broke"
   *  and reporting what broke — which is what a message alone cost once. */
  | { status: 'error'; message: string; detail: string | null; needsRepair: boolean };

let state: VaultState = { status: 'disconnected' };
const listeners = new Set<() => void>();
/** Bumped on every open, so a sync that lands after a newer one started is
 *  dropped rather than publishing a vault the session has moved past. */
let generation = 0;
/** The freshness watch belongs to the vault that is open, so re-opening ends
 *  the previous one rather than leaving two tabs' worth of probes running. */
let stopWatching: (() => void) | null = null;

/** Identity is the subscription contract: useSyncExternalStore re-renders when
 *  this changes, so it must change only when the state actually did. */
function publish(next: VaultState): void {
  state = next;
  for (const listener of listeners) listener();
}

function open(): void {
  generation += 1;
  const mine = generation;
  // Nothing may push while there is no vault to push through, and a stale
  // flusher holds the previous session's key.
  setFlusher(null);
  stopWatching?.();
  stopWatching = null;

  const session = getSession();
  if (!session) {
    publish({ status: 'disconnected' });
    return;
  }

  publish({ status: 'loading' });
  const backend = backendFor(session.grantSecret);
  const vault = new CloudVault(backend, session.dataKey, sealedIndexedDbCache(session.dataKey));
  // The queue outlives any screen, so it is handed a way to write rather than
  // owning one — and it is handed it here, once, rather than by whichever
  // screens happen to be on.
  const arm = () => setFlusher((writes) => writeFiles(vault, backend, session.dataKey, writes));

  // Armed on BOTH ways out of the open — a tab that could not refresh needs the
  // poll more than a healthy one does, because the poll is the only thing that
  // will notice it can read again. Without it a stale tab stays stale until
  // someone reloads, which is a worse answer than the silence it replaced.
  const startWatching = () => {
    // The probe is the same gated read every other call makes, so a refusal
    // here means the grant was withdrawn rather than that the network went.
    stopWatching = watchFreshness({
      held: () => vault.manifestVersion,
      onBehind: () => void adoptRemoteChanges(),
      probe: async () => {
        try {
          const { envelope } = await backend.call<{ envelope: { manifestVersion: number } | null }>(
            'getVaultManifest',
            {},
          );
          // Getting through is itself the news when the last refresh did not:
          // whatever the version says, this tab can reach the vault again.
          clearStale();
          return { kind: 'version', version: envelope?.manifestVersion ?? 0 };
        } catch (error) {
          if (!refused(error)) return { kind: 'unreachable' };
          announceCutOff(session.grantSecret);
          return { kind: 'cut-off' };
        }
      },
    });
  };

  void (async () => {
    // A tab let in by a sibling arrives knowing which manifest that sibling
    // had, so it can open that version out of the cache and show something
    // immediately. Waiting on the round trip was most of what made a second
    // tab feel like a fresh connection.
    const warm = takeWarmManifestVersion();
    let painted = false;
    if (warm !== null && (await vault.warmStart(warm))) {
      if (mine !== generation) return;
      arm();
      publish({ status: 'ready', vault, stale: null });
      painted = true;
    }

    try {
      const before = vault.manifestVersion;
      await vault.sync();
      if (mine !== generation) return;
      arm();
      // Re-publishing an unchanged vault re-renders every surface for nothing.
      // A version that moved is a real change and has to reach them.
      if (!painted || vault.manifestVersion !== before) publish({ status: 'ready', vault, stale: null });
      markCurrent();
      // Asked only once the tab has something to be stale ABOUT.
      startWatching();
    } catch (error: unknown) {
      if (mine !== generation) return;
      // A warm tab is already usable and already writable, so a refresh that
      // could not reach the backend must not replace a working page with an
      // error screen. It must still SAY so: what is on screen came out of the
      // cache, and every read after this one will fail the same way.
      if (painted) {
        publish({ status: 'ready', vault, stale: describeFailure(error).message });
        // A refusal is terminal, so there is nothing for the poll to notice.
        return refused(error) ? cutOff(session) : startWatching();
      }
      publish({ status: 'error', ...describeFailure(error) });
      if (refused(error)) cutOff(session);
    }
  })();
}

const refused = (error: unknown): boolean => error instanceof CallableError && error.needsRepair;

/** Terminal until a new session opens: the poll stops, and the tab stops
 *  answering siblings' asks. */
function markThisTabCutOff(): void {
  stopWatching?.();
  stopWatching = null;
  markCutOff();
}

/** The grant behind [session] is dead. Siblings holding it are told, so no tab
 *  of this browser hands the key to the next one that reloads. */
function cutOff(session: VaultSession): void {
  markThisTabCutOff();
  announceCutOff(session.grantSecret);
}

subscribeSession(open);
open();

// Armed for the lifetime of the tab rather than per open, because the question
// a sibling asks is "is anyone ready", and only a ready tab may answer it. A
// ready tab whose grant was refused is not one: what it would hand over opens
// the cache and nothing else.
servePeers(
  () =>
    state.status === 'ready' && getSession() && !freshnessSnapshot().cutOff
      ? { session: getSession()!, manifestVersion: state.vault.manifestVersion, account: getAuth() }
      : null,
  markThisTabCutOff,
);

/**
 * Take what the vault now holds — everything except what this tab is still
 * writing.
 *
 * Per PATH, never per vault. The queue's `pending` map is exactly "the files
 * this tab owns right now", and `loadPlan` already lays it over whatever a read
 * returns, so adopting is mostly a matter of syncing and letting the surfaces
 * re-read: an arc changed elsewhere appears, and one being edited here does
 * not flicker to the other version and back.
 *
 * The case that cannot be decided is a path in `pending` that ALSO moved
 * elsewhere. Nothing is merged — this tab's copy wins the read and will
 * overwrite the other version when it flushes — so the collision is named
 * rather than resolved.
 */
export async function adoptRemoteChanges(): Promise<void> {
  const current = state;
  if (current.status !== 'ready') return;
  const vault = current.vault;
  const mine = generation;

  const owned = [...queueSnapshot().pending.keys()];
  const before = new Map(owned.map((path) => [path, vault.fileVersion(path)]));
  const at = vault.manifestVersion;

  try {
    await vault.sync();
  } catch {
    // The probe will come round again; a refresh that could not reach the
    // backend is not worth disturbing a working page for.
    return;
  }
  // A re-open while this was in flight means the vault below has been replaced.
  if (mine !== generation || state.status !== 'ready') return;
  if (vault.manifestVersion === at) return markCurrent();

  // Published before the conflicts are worked out, so the surfaces re-read as
  // soon as there is something to re-read. A sync that got through also clears
  // any stale mark: the tab is current again by definition.
  publish({ status: 'ready', vault, stale: null });
  markConflicts(owned.filter((path) => vault.fileVersion(path) !== before.get(path)));
}

/** Drops the stale mark once the tab can reach the vault again. */
function clearStale(): void {
  if (state.status === 'ready' && state.stale !== null) publish({ ...state, stale: null });
}

export function getVaultState(): VaultState {
  return state;
}

export function subscribeVault(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Re-open and re-sync. Shared, so one surface asking re-reads for all of them —
 * which is the point: a re-read that only refreshed the screen you asked from
 * is how the counts drifted apart in the first place.
 */
export function reloadVault(): void {
  open();
}
